import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import { useModuleLicense } from '../../../hooks/useModuleLicense';
import pbrService from '../../../services/pbr';
import DeadlineChip from '../../../components/cultivar/pbr/DeadlineChip';
import PbrStageBadge from '../../../components/cultivar/pbr/PbrStageBadge';
import type { PbrCandidate } from '../../../types/cultivar';
import { ListChecks, Plus, ShieldAlert } from 'lucide-react';

export default function PbrCandidates() {
  const navigate = useNavigate();
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar');
  const [rows, setRows] = useState<PbrCandidate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasAccess) { setLoading(false); return; }
    pbrService.listCandidates()
      .then(r => { if (r.success && r.data) setRows(r.data); })
      .finally(() => setLoading(false));
  }, [hasAccess]);

  if (licLoading) return <div className="p-6 text-neutral-500">Loading…</div>;
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ShieldAlert className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="PBR Candidates"
        subtitle="Cultivars flagged for a PBR application"
        icon={<ListChecks className="h-6 w-6 text-white" />}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-neutral-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
            <p className="text-neutral-600">No PBR candidates yet.</p>
            <p className="text-sm text-neutral-500 mt-1">
              Edit a cultivar and tick "Candidate for PBR" to add it here.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Cultivar</th>
                  <th className="px-4 py-3 font-medium">First sale AU</th>
                  <th className="px-4 py-3 font-medium">First sale overseas</th>
                  <th className="px-4 py-3 font-medium">US patent</th>
                  <th className="px-4 py-3 font-medium">Part 1 deadline</th>
                  <th className="px-4 py-3 font-medium">Applications</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rows.map(c => (
                  <tr key={c.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{c.trade_name || c.name}</div>
                      <div className="text-xs text-neutral-500">{c.botanical_name || c.species || ''}</div>
                    </td>
                    <td className="px-4 py-3">{c.first_sale_date_au || '—'}</td>
                    <td className="px-4 py-3">
                      {c.first_sale_date_overseas
                        ? `${c.first_sale_date_overseas}${c.first_sale_country_overseas ? ` (${c.first_sale_country_overseas})` : ''}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">{c.us_patent_number || '—'}</td>
                    <td className="px-4 py-3"><DeadlineChip deadline={c.part1_deadline} label="Due" /></td>
                    <td className="px-4 py-3">
                      {c.applications && c.applications.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {c.applications.map(a => (
                            <button
                              key={a.id}
                              onClick={() => navigate(`/cultivar/pbr/applications/${a.id}`)}
                              className="flex items-center gap-1 text-xs hover:underline"
                              title={`${a.jurisdiction} ${a.application_type}`}
                            >
                              <span>{a.jurisdiction}</span>
                              <PbrStageBadge stage={a.stage} />
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-neutral-400">None</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/cultivar/pbr/applications/new?cultivar_id=${c.id}`)}
                      >
                        <Plus className="h-4 w-4 mr-1" /> New application
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
